import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConversationContentPreview } from '../types/Conversation';
import { resolveInstagramPreviewPermalink } from '../utils/instagramPermalink';
import styles from './InstagramContentPreviewCard.module.css';

interface InstagramContentPreviewCardProps {
  preview: ConversationContentPreview;
}

const getContentTypeLabelKey = (preview: ConversationContentPreview): string => {
  const productType = preview.mediaProductType?.trim().toUpperCase();
  if (productType === 'REELS' || productType === 'REEL') {
    return 'instagramContentPreviewReel';
  }

  if (productType === 'STORY') {
    return 'instagramContentPreviewStory';
  }

  const contentType = preview.contentType?.trim().toUpperCase();
  if (contentType === 'VIDEO') {
    return 'instagramContentPreviewVideo';
  }

  return 'instagramContentPreviewPost';
};

const getShareBadgeKey = (preview: ConversationContentPreview): string | null => {
  if (preview.shareKind?.trim().toLowerCase() !== 'dm_share') {
    return null;
  }

  return preview.ownership?.trim().toLowerCase() === 'owned'
    ? 'instagramContentPreviewYourPost'
    : 'instagramContentPreviewSharedPost';
};

export const InstagramContentPreviewCard: React.FC<InstagramContentPreviewCardProps> = ({ preview }) => {
  const { t } = useTranslation('conversations');
  const shareBadgeKey = getShareBadgeKey(preview);
  const labelKey = shareBadgeKey ?? getContentTypeLabelKey(preview);
  const caption = preview.caption?.trim();
  const isSharedPost = Boolean(shareBadgeKey);
  const permalink = resolveInstagramPreviewPermalink(preview);

  return (
    <div className={`${styles.card}${isSharedPost && preview.ownership !== 'owned' ? ` ${styles.cardShared}` : ''}`}>
      {preview.thumbnailUrl ? (
        <img
          className={styles.thumbnail}
          src={preview.thumbnailUrl}
          alt={caption || t('instagramContentPreviewImageAlt')}
        />
      ) : (
        <div className={styles.thumbnailPlaceholder} aria-hidden="true">IG</div>
      )}
      <div className={styles.body}>
        <div className={styles.eyebrow}>
          {t(labelKey)}
        </div>
        {caption ? (
          <p className={styles.caption}>{caption}</p>
        ) : (
          <p className={styles.captionMuted}>{t('instagramContentPreviewNoCaption')}</p>
        )}
        {permalink ? (
          <a
            className={styles.link}
            href={permalink}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('instagramContentPreviewViewOnInstagram')}
          </a>
        ) : null}
      </div>
    </div>
  );
};

export default InstagramContentPreviewCard;
